import http from '../services/http';

// Pixel transparente: e o que fica no `src` ate o download autenticado
// voltar. Antes, o src inicial era a URL publica em /storage -- entao o
// navegador buscava o anexo SEM autenticacao a cada renderizacao, e o blob
// so aparecia depois. O usuario via o blob no fim e a requisicao publica
// acontecia do mesmo jeito.
const PIXEL_TRANSPARENTE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

// O caminho do anexo viaja num atributo, ja que o src deixou de conte-lo.
const ATRIBUTO_CAMINHO = 'data-anexo';

const caminhoDaTag = (tag) => {
    const doAtributo = tag.match(new RegExp(`\\b${ATRIBUTO_CAMINHO}=(["'])([^"']+)\\1`, 'i'));
    if (doAtributo) return doAtributo[2];

    // HTML antigo, gravado antes desta mudanca: o caminho ainda esta no src.
    const doSrc = tag.match(/\bsrc=(["'])([^"']+)\1/i);
    return doSrc ? extractTicketAttachmentPath(doSrc[2]) : null;
};

// Este arquivo NAO monta mais URL publica de anexo. As funcoes que faziam
// isso (filesPublicBase, resolveTicketCommentImageUrl,
// buildTicketInlineImageUrl) foram removidas: estavam sem uso e um anexo de
// chamado so pode ser lido pelo endpoint autenticado, que confere se ele
// pertence a esta empresa. Se voltar a ser preciso exibir um anexo, o
// caminho e fetchAuthImageBlobUrl -- nunca /storage.
const authImageBlobCache = new Map();
const authImageDataUrlCache = new Map();

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

export const extractTicketAttachmentPath = (srcOrPath) => {
    if (!srcOrPath) return null;
    const raw = String(srcOrPath).trim();
    if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) return null;

    const storageMatch = raw.match(/(?:https?:\/\/[^"'\s?#]+)?\/?storage\/(ticket-attachments\/[^"'\s?#]+)/i);
    if (storageMatch?.[1]) return storageMatch[1];

    const pathMatch = raw.match(/^(ticket-attachments\/[^"'\s?#]+)/i);
    if (pathMatch?.[1]) return pathMatch[1];

    const anyMatch = raw.match(/(ticket-attachments\/[^"'\s?#]+)/i);
    return anyMatch?.[1] || null;
};

/**
 * Prepara o HTML para a PRIMEIRA renderizacao, sem disparar nenhuma
 * requisicao ao /storage publico.
 *
 * O anexo de chamado so pode ser lido pelo endpoint autenticado, que
 * confere se ele pertence a esta empresa. Deixar a URL publica aqui, ainda
 * que por um instante, e o mesmo que servir o arquivo sem autenticacao --
 * o navegador busca antes de o blob chegar.
 */
export const rewriteTicketCommentHtmlImages = (html) => {
    if (!html || typeof html !== 'string') return html || '';

    return html.replace(/<img\b([^>]*?)\ssrc=(["'])([^"']+)\2([^>]*)>/gi, (full, before, quote, src, after) => {
        if (src.startsWith('data:') || src.startsWith('blob:')) return full;

        const path = extractTicketAttachmentPath(src);

        // Imagem que nao e anexo nosso (link externo colado numa acao) fica
        // como esta -- nao cabe a nos intermediar.
        if (!path) return full;

        return `<img${before} src=${quote}${PIXEL_TRANSPARENTE}${quote} ${ATRIBUTO_CAMINHO}=${quote}${path}${quote}${after}>`;
    });
};

export const normalizeInlineImageParagraphs = (html) => {
    if (!html || typeof html !== 'string') return html || '';

    let result = html.replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, '');
    let prev = '';

    while (prev !== result) {
        prev = result;
        result = result.replace(/<\/p>\s*<p>\s*(<img\b[^>]*>)\s*<\/p>\s*<p>/gi, '$1');
        result = result.replace(/<\/p>\s*<p>\s*(<img\b[^>]*>)\s*<\/p>(?!\s*<p>)/gi, '$1');
        result = result.replace(/<p>\s*(<img\b[^>]*>)\s*<\/p>\s*<p>/gi, '$1');
    }

    return result;
};

const fetchAuthImageBlobUrl = async (path) => {
    if (authImageBlobCache.has(path)) {
        return authImageBlobCache.get(path);
    }

    const res = await http.get(`/ticket/comments/file?path=${encodeURIComponent(path)}`, {
        responseType: 'blob',
    });
    const blobUrl = URL.createObjectURL(res.data);
    authImageBlobCache.set(path, blobUrl);
    return blobUrl;
};

const fetchAuthImageDataUrl = async (path) => {
    if (authImageDataUrlCache.has(path)) {
        return authImageDataUrlCache.get(path);
    }

    const res = await http.get(`/ticket/comments/file?path=${encodeURIComponent(path)}`, {
        responseType: 'blob',
    });
    const dataUrl = await blobToDataUrl(res.data);
    authImageDataUrlCache.set(path, dataUrl);
    return dataUrl;
};

export const loadTicketCommentHtmlWithAuthImages = async (html) => {
    if (!html || typeof html !== 'string') {
        return html || '';
    }

    let processed = normalizeInlineImageParagraphs(html);
    const imgTagRegex = /<img\b[^>]*>/gi;
    const tags = processed.match(imgTagRegex) || [];

    for (const tag of tags) {
        const srcMatch = tag.match(/\bsrc=(["'])([^"']+)\1/i);
        if (!srcMatch) continue;

        const quote = srcMatch[1];
        const src = srcMatch[2];
        if (src.startsWith('blob:')) continue;

        // O caminho vem do atributo (o src agora e so um pixel).
        const path = caminhoDaTag(tag);
        if (!path) continue;

        try {
            const blobUrl = await fetchAuthImageBlobUrl(path);
            const newTag = tag.replace(`src=${quote}${src}${quote}`, `src=${quote}${blobUrl}${quote}`);
            processed = processed.replace(tag, newTag);
        } catch (_) {
        }
    }

    return processed;
};

export const loadTicketCommentHtmlForQuillEditor = async (html) => {
    if (!html || typeof html !== 'string') {
        return html || '';
    }

    let processed = normalizeInlineImageParagraphs(html);
    const imgTagRegex = /<img\b[^>]*>/gi;
    const tags = processed.match(imgTagRegex) || [];

    for (const tag of tags) {
        const srcMatch = tag.match(/\bsrc=(["'])([^"']+)\1/i);
        if (!srcMatch) continue;

        const quote = srcMatch[1];
        const src = srcMatch[2];

        const path = caminhoDaTag(tag);
        if (!path) {
            // Sem caminho de anexo: se ja e um data: pronto, mantem; senao
            // remove, como antes.
            if (!src.startsWith('data:')) {
                processed = processed.replace(tag, '');
            }
            continue;
        }

        try {
            const dataUrl = await fetchAuthImageDataUrl(path);
            const newTag = tag.replace(`src=${quote}${src}${quote}`, `src=${quote}${dataUrl}${quote}`);
            processed = processed.replace(tag, newTag);
        } catch (_) {
            processed = processed.replace(tag, '');
        }
    }

    return processed;
};
